import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--paper-types-table"
export default class extends Controller {
    static targets = [
        "table",
        "createModal",
        "createForm",
        "nameInput",
        "editModal",
        "editForm",
        "editNameInput",
        "editIdInput"
    ]

    connect() {
        this.initializeTable()
        this.loadPaperTypes()
        this._setupRowDoubleClick()
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _setupRowDoubleClick() {
        if (!this.hasTableTarget) return
        this.tableTarget.addEventListener('dblclick', (e) => {
            const tr = e.target.closest('tbody tr')
            if (!tr) return
            const argusTable = this.application.getControllerForElementAndIdentifier(this.tableTarget, 'argus--table')
            if (!argusTable) return
            const rows = Array.from(this.tableTarget.querySelectorAll('tbody tr'))
            const index = rows.indexOf(tr)
            const data = argusTable.getData()
            const row = data[index]
            if (row && row.id) {
                window.location.href = `/settings/paper-types/${row.id}`
            }
        })
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadPaperTypes() {
        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            const response = await fetch('/api/print_mappings/paper_type/list', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch paper types')
            }

            const data = await response.json()
            this.populateTable(data.paper_types || [])
        } catch (error) {
            console.error('Error loading paper types:', error)
            sendFailureNotification('Failed to load paper types')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(paperTypes) {
        if (this.hasTableTarget) {
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(paperTypes)
            }
        }
    }

    openCreateModal(event) {
        event.preventDefault()
        
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }

        // Re-enable submit button (Rails UJS disables it after first submission)
        const submitBtn = this.createModalTarget.querySelector('input[type="submit"], button[type="submit"]')
        if (submitBtn) {
            submitBtn.disabled = false
        }

        // Show modal
        const modal = bootstrap.Modal.getOrCreateInstance(this.createModalTarget)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.createFormTarget)

        try {
            const response = await fetch('/api/print_mappings/paper_type/create', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to create paper type')
            }

            // Close modal
            const modal = bootstrap.Modal.getInstance(this.createModalTarget)
            modal.hide()

            // Reload table
            await this.loadPaperTypes()

            sendSuccessNotification('Paper type created successfully')
        } catch (error) {
            console.error('Error creating paper type:', error)
            sendFailureNotification('Failed to create paper type')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const paperTypeId = button.dataset.paperTypeId

        // Fetch paper type details
        this.fetchPaperTypeDetails(paperTypeId)
    }

    async fetchPaperTypeDetails(paperTypeId) {
        try {
            const response = await fetch(`/api/print_mappings/paper_type/describe?id=${paperTypeId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch paper type details')
            }

            const data = await response.json()
            const paperType = data.paper_types[0]

            // Populate edit form
            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = paperType.name || ''
            }
            if (this.hasEditIdInputTarget) {
                this.editIdInputTarget.value = paperType.id
            }

            // Show modal
            const modal = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
            modal.show()
        } catch (error) {
            console.error('Error fetching paper type details:', error)
            sendFailureNotification('Failed to load paper type details')
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.editFormTarget)

        try {
            const response = await fetch('/api/print_mappings/paper_type/update', {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to update paper type')
            }

            // Close modal
            const modal = bootstrap.Modal.getInstance(this.editModalTarget)
            modal.hide()

            // Reload table
            await this.loadPaperTypes()

            sendSuccessNotification('Paper type updated successfully')
        } catch (error) {
            console.error('Error updating paper type:', error)
            sendFailureNotification('Failed to update paper type')
        }
    }

    async deletePaperType(event) {
        event.preventDefault()

        const button = event.currentTarget
        const paperTypeId = button.dataset.paperTypeId

        // Get paper type name from table row
        const row = button.closest('tr')
        const nameCell = row.querySelector('td:first-child')
        const paperTypeName = nameCell ? nameCell.textContent : 'this paper type'

        if (!confirm(`Are you sure you want to delete ${paperTypeName}?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', paperTypeId)

        try {
            const response = await fetch('/api/print_mappings/paper_type/delete', {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to delete paper type')
            }

            // Reload table
            await this.loadPaperTypes()

            sendSuccessNotification('Paper type deleted successfully')
        } catch (error) {
            console.error('Error deleting paper type:', error)
            sendFailureNotification('Failed to delete paper type')
        }
    }

    viewPrintClasses(event) {
        event.preventDefault()

        const button = event.currentTarget
        const paperTypeId = button.dataset.paperTypeId

        // Navigate to print classes page
        window.location.href = `/settings/paper-types/${paperTypeId}`
    }
}
